import { useCallback, useEffect, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Stack,
	Typography,
} from '@mui/material'
import entitlementStore from '../../stores/entitlementStore'
import {
	clearPendingOrder,
	createBillingOrder,
	formatOfferMoney,
	getBillingOrder,
	getBillingOffers,
	readPendingOrder,
	redirectToPayment,
	savePendingOrder,
	shouldPollOrder,
} from '../../utils/billingClient'
import { canPurchaseFromCatalog, isPurchaseUnavailable } from './billingUiPolicy'

const STATE_ALERTS = {
	pending: { severity: 'info', text: 'Ожидаем подтверждение оплаты от платёжной системы.' },
	expired: { severity: 'warning', text: 'Локальный срок заказа истёк. Если деньги были списаны, подтверждение всё ещё будет обработано.' },
	completed: { severity: 'success', text: 'Оплата подтверждена. Доступ обновлён сервером.' },
	reconciliation_required: { severity: 'warning', text: 'Платёж требует проверки. Обратитесь в поддержку и сообщите номер заказа.' },
}

const Payment = () => {
	const [catalog, setCatalog] = useState(null)
	const [order, setOrder] = useState(null)
	const [loading, setLoading] = useState(true)
	const [creatingOfferId, setCreatingOfferId] = useState(null)
	const [error, setError] = useState(null)

	const refreshOrder = useCallback(async orderId => {
		try {
			const current = await getBillingOrder(orderId)
			setOrder(current)
			if (current.state === 'completed') {
				clearPendingOrder()
				await entitlementStore.fetch({ force: true })
			}
			if (current.state === 'reconciliation_required') clearPendingOrder()
			return current
		} catch (requestError) {
			if (requestError?.response?.status === 404) clearPendingOrder()
			if (isPurchaseUnavailable(requestError)) {
				clearPendingOrder()
				return null
			}
			setError(requestError?.response?.data?.error?.code || 'BILLING_STATUS_UNAVAILABLE')
			return null
		}
	}, [])

	useEffect(() => {
		let active = true
		let timer
		const start = async () => {
			try {
				const offers = await getBillingOffers()
				if (!active) return
				setCatalog(offers)
				const pendingId = readPendingOrder()
				if (!pendingId) return
				const poll = async () => {
					const current = await refreshOrder(pendingId)
					if (active && current && shouldPollOrder(current.state)) {
						timer = window.setTimeout(poll, 3000)
					}
				}
				await poll()
			} catch (requestError) {
				if (!active) return
				if (isPurchaseUnavailable(requestError)) {
					clearPendingOrder()
					setCatalog({ offers: [], canPurchase: false })
				}
				else setError(requestError?.response?.data?.error?.code || 'BILLING_OFFERS_UNAVAILABLE')
			} finally {
				if (active) setLoading(false)
			}
		}
		start()
		return () => {
			active = false
			if (timer) window.clearTimeout(timer)
		}
	}, [refreshOrder])

	const startPayment = async offerId => {
		if (!canPurchaseFromCatalog(catalog)) return
		setCreatingOfferId(offerId)
		setError(null)
		try {
			const result = await createBillingOrder(offerId)
			savePendingOrder(result.order.id)
			setOrder(result.order)
			redirectToPayment(result.paymentUrl)
		} catch (requestError) {
			setError(requestError?.response?.data?.error?.code || 'BILLING_ORDER_UNAVAILABLE')
			setCreatingOfferId(null)
		}
	}

	if (loading) return <CircularProgress size={24} sx={{ mt: 2 }} />
	// ADMIN/MEMBER and PERSONAL retain the Organization entitlement summary in
	// PaymentPage. V1 merely has no purchase control for them; it makes no
	// claim about future PERSONAL commercial capabilities.
	if (!canPurchaseFromCatalog(catalog)) return null

	const stateAlert = order ? STATE_ALERTS[order.state] : null
	return (
		<Box sx={{ mt: 2 }}>
			<Typography variant='h5' gutterBottom>Варианты оплаты</Typography>
			{error && <Alert severity='error' sx={{ mb: 2 }}>Оплата недоступна ({error}).</Alert>}
			{stateAlert && (
				<Alert severity={stateAlert.severity} sx={{ mb: 2 }}>
					{stateAlert.text} Номер заказа: {order.id}
				</Alert>
			)}
			{!catalog?.offers?.length && (
				<Alert severity='info'>Сейчас нет доступных предложений оплаты.</Alert>
			)}
			<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
				{catalog?.offers?.map(offer => (
					<Card key={offer.id} sx={{ minWidth: 260 }}>
						<CardContent>
							<Typography variant='h6'>{offer.plan.name}</Typography>
							<Typography variant='h5' sx={{ mt: 1 }}>
								{formatOfferMoney(offer.amountMinor, offer.currency)}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								Доступ на {offer.entitlementDurationMonths} мес.
							</Typography>
						</CardContent>
						<CardActions>
							<Button
								variant='contained'
								disabled={!catalog.paymentAvailable || creatingOfferId !== null}
								onClick={() => startPayment(offer.id)}
							>
								{creatingOfferId === offer.id ? 'Создаём заказ…' : 'Оплатить'}
							</Button>
						</CardActions>
					</Card>
				))}
			</Stack>
			{catalog?.offers?.length > 0 && !catalog.paymentAvailable && (
				<Alert severity='info' sx={{ mt: 2 }}>Платёжный провайдер пока не настроен.</Alert>
			)}
		</Box>
	)
}

export default Payment
